import { JSX, useContext, useEffect, useRef, useState } from "react";
import { ColumnConfig, numberSort, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Link, useHistory } from "react-router-dom";
import { Message, MessageType, ReportCompletePayload } from "@app/notifications";
import { NotificationContext } from "@app/notifications/NotificationContext";
import { BulkProviderChangeReportResponseModel } from "@app/shared/provider-change-wizards/interfaces";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { Accordion } from "@app/shared/Accordion";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { convertDateTime } from "@app/shared/convertDateTime";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import ExportService from "@app/exports/exportService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { BulkProjectTypeEnum } from "@app/shared/enums/bulkProjectTypeEnum";

export const VOLUME_TERMINATION_TITLE = "Volume Termination";

interface BulkProviderChangeReportsProps {
  buttonTerm: string;
  endpoint: () => Promise<BulkProviderChangeReportResponseModel[]>;
  title: string;
  zipTargetingEnabled?: boolean;
  zipTerminationEnabled?: boolean;
}

interface ProviderChangeReportsTableData {
  [wafflesKeyColumnName]: string;
  createdBy: string;
  date: string;
  download: JSX.Element | number;
  format: string;
  numberOfProjects: number;
  reportName: string;
  status: string;
  projectNames: string;
}

const providerChangeColumnConfig: ColumnConfig = {
  createdBy: {
    displayText: "Created By",
    testId: "created-by",
  },
  date: {
    displayText: "Date",
    testId: "date",
  },
  download: {
    displayText: "Report",
    noSort: true,
    testId: "report",
  },
  format: {
    displayText: "Format",
    testId: "format",
  },
  numberOfProjects: {
    displayText: "Projects",
    testId: "projects",
  },
  projectNames: {
    displayText: "Project Name(s)",
    testId: "project-names"
  },
  reportName: {
    displayText: "Report Name",
    testId: "report-name",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
};

const customSort = (records: ProviderChangeReportsTableData[], column: keyof ProviderChangeReportsTableData, sortDirection: SortDirections) => {
  let newRecords = [...records];
  const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

  switch (column) {
    case "date":
      newRecords = newRecords.sort((a, b) => (new Date(a.date) > new Date(b.date) ? 1 : -1) * multiplier);
      break;
    case "createdBy":
    case "format":
    case "projectNames":
    case "reportName":
    case "status":
      newRecords = newRecords.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      break;
    case "numberOfProjects":
      newRecords = numberSort(newRecords, column, multiplier);
      break;
    default:
      break;
  }

  return newRecords;
};

const BulkProviderChangeReports: React.FC<React.PropsWithChildren<BulkProviderChangeReportsProps>> = (props: BulkProviderChangeReportsProps) => {
  const [reportsToday, setReportsToday] = useState<ProviderChangeReportsTableData[]>([]);
  const [reportsLast7, setReportsLast7] = useState<ProviderChangeReportsTableData[]>([]);
  const [reportsLast30, setReportsLast30] = useState<ProviderChangeReportsTableData[]>([]);
  const [isCountyVolumeProviderTerminationEligible, setIsCountyVolumeProviderTerminationEligible] = useState<boolean>(false);
  const [isGhostVolumeProviderTerminationEligible, setIsGhostVolumeProviderTerminationEligible] = useState<boolean>(false);
  const [isZipVolumeProviderTerminationEligible, setIsZipVolumeProviderTerminationEligible] = useState<boolean>(false);
  const titleSlug = useRef<string>(props.title.toLowerCase().replace(/ /g, "-")).current;
  const downloadRef = useRef<HTMLSpanElement>(null);
  const eligibilityRequestId = useRef<number>(0);
  const notificationContext = useContext(NotificationContext);
  const history = useHistory();
  const providerTerminationService = new ProviderTerminationService();

  useEffect(() => {
    // subscribe and unsubscribe from notifications
    notificationContext.subscribe(handleNotifications);
    return () => notificationContext.unsubscribe(handleNotifications);
  }, [reportsToday, reportsLast7, reportsLast30, props.zipTerminationEnabled, props.title]);

  useEffect(() => {
    updateReports();
  }, []);

  useEffect(() => {
    getBulkProviderTerminationAvailability();
  }, [props.zipTerminationEnabled]);

  const handleNotifications = async (message: Message) => {
    if (message && message.notificationType === MessageType.ReportComplete) {
      const payload = message.payload as ReportCompletePayload;
      // A completed termination changes which projects are eligible, so refresh the menu state too.
      getBulkProviderTerminationAvailability();
      if ([...reportsToday, ...reportsLast7, ...reportsLast30].map((r) => r[wafflesKeyColumnName]).indexOf(payload.reportId.toString()) > -1) {
        updateReports();
      }
    }
  };

  const updateReports = () => {
    props.endpoint().then((response: BulkProviderChangeReportResponseModel[]) => {
      const now = new Date();
      // now - (7 days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
      // then we set the time on that day to 00:00:00.0000 just to be fair
      const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      // setHours updates the month object but returns the ms since the epoch instead of the month object
      sevenDaysAgo.setHours(0, 0, 0, 0);
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      const tempReportsToday: ProviderChangeReportsTableData[] = [];
      const tempReportsLast7: ProviderChangeReportsTableData[] = [];
      const tempReportsLast30: ProviderChangeReportsTableData[] = [];

      const convertExportModelToRecord = (exportModel: BulkProviderChangeReportResponseModel) => {

        const timestamp = convertDateTime(exportModel.createDateUtc).toLocaleString("en-US");

        /* oxlint-disable sort-keys */
        const tableRow = {
          key: exportModel.reportInfoId.toString(),
          date: timestamp,
          createdBy: exportModel.createdBy,
          numberOfProjects: exportModel.projectCount,
          projectNames: exportModel.projectNames?.join(", ") ?? "",
          format: exportModel.format,
          status: exportModel.reportStatus.charAt(0).toUpperCase() + exportModel.reportStatus.slice(1),
          reportName: exportModel.reportName,
          download: exportModel.showDownloadLink ? (
            <QESButton
              qesType="secondary"
              qesTestId={`${titleSlug}-report-download-${exportModel.reportInfoId}`}
              onClick={() => downloadReport(exportModel.reportInfoId)}>Download
            </QESButton>
          ) : null,
        };
        /* oxlint-enable sort-keys */
        return tableRow;
      };

      response.forEach((r) => {
        const reportTime = convertDateTime(r.createDateUtc);
        if (now.getDate() === reportTime.getDate() && now.getMonth() === reportTime.getMonth() && now.getFullYear() === reportTime.getFullYear()) {
          tempReportsToday.push(convertExportModelToRecord(r));
        } else if (reportTime < now && reportTime > sevenDaysAgo) {
          tempReportsLast7.push(convertExportModelToRecord(r));
        } else if (reportTime < sevenDaysAgo && reportTime > thirtyDaysAgo) {
          tempReportsLast30.push(convertExportModelToRecord(r));
        }
      });
      setReportsToday(tempReportsToday);
      setReportsLast7(tempReportsLast7);
      setReportsLast30(tempReportsLast30);
    });
  };

  const downloadReport = (reportId: number) => {
    const exportService = new ExportService();
    const link = `${exportService.getReportLink(reportId)}`;
    authenticatedDownload(link);
  }

  const getBulkProviderTerminationAvailability = (): void => {
    if (props.title === VOLUME_TERMINATION_TITLE) {
      const requestId = ++eligibilityRequestId.current;
      // County eligibility gated only on Volume Termination access.
      const countyEligibility = providerTerminationService.getBulkTerminationProjects(false);
      // Ghost Terminations is PCI-specific, so it's the only path that is gated on
      // having at least one PCI-enabled county project.
      const ghostEligibility = providerTerminationService.getBulkTerminationProjects(true);
      // Zip eligibility gated on Volume Termination access and access to zip projects.
      const zipEligibility = props.zipTerminationEnabled
        ? providerTerminationService.getBulkTerminationProjects(false, BulkProjectTypeEnum.Zip)
        : Promise.resolve([]);

      Promise.all([countyEligibility, ghostEligibility, zipEligibility]).then(([countyProjects, ghostProjects, zipProjects]) => {
        if (requestId !== eligibilityRequestId.current) return;
        setIsCountyVolumeProviderTerminationEligible(countyProjects?.length > 0);
        setIsGhostVolumeProviderTerminationEligible(ghostProjects?.length > 0);
        setIsZipVolumeProviderTerminationEligible(zipProjects?.length > 0);
      }).catch(() => {
        // Leave the current eligibility state in place if the refresh fails.
      });
    }
  }

  return (
    <Page documentTitle={`${props.title} Reports`}>
      <Breadcrumbs items={[props.title]} />
      <PageInner>
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0 col-auto px-0">{props.title}</h1>
          {props.title === VOLUME_TERMINATION_TITLE ?
            <NewActionMenu
              alignRight={true}
              containerClassnames="col-auto"
              buttonClassNames="qes-button-primary ms-3"
              buttonText="Terminations"
              disableMaxHeight={true}
              testId={`${titleSlug}__dropdown`}
            >
              <NewActionMenuItem
                key="import"
                display={props.zipTerminationEnabled ? "New County Terminations" : "New Terminations"}
                testId={`${titleSlug}__new_import`}
                disabled={!isCountyVolumeProviderTerminationEligible}
                onClick={() => history.push('./import')}
              />
              {props.zipTerminationEnabled &&
                <NewActionMenuItem
                  key="zip"
                  display="New Zip Code Terminations"
                  disabled={!isZipVolumeProviderTerminationEligible}
                  testId={`${titleSlug}__new_zip`}
                  onClick={() => history.push(`./import?projectType=${BulkProjectTypeEnum.Zip}`)}
                />
              }
              <NewActionMenuItem
                key="ghost"
                display="Ghost Terminations"
                disabled={!isGhostVolumeProviderTerminationEligible}
                testId={`${titleSlug}__new_ghost`}
                onClick={() => history.push("./ghost")}
                />
            </NewActionMenu>
            : props.zipTargetingEnabled ?
            <NewActionMenu
              alignRight={true}
              containerClassnames="col-auto"
              buttonClassNames="qes-button-primary ms-3"
              buttonText="Targeting"
              disableMaxHeight={true}
              testId={`${titleSlug}__dropdown`}
            >
              <NewActionMenuItem
                key="county"
                display="New County Targeting"
                disabled={false}
                testId={`${titleSlug}__new_county`}
                onClick={() => history.push('./import')}
              />
              <NewActionMenuItem
                key="zip"
                display="New Zip Code Targeting"
                disabled={false}
                testId={`${titleSlug}__new_zip`}
                onClick={() => history.push(`./import?projectType=${BulkProjectTypeEnum.Zip}`)}
              />
            </NewActionMenu>
            :
            <Link to="./import" className="col-auto px-0"><QESButton qesTestId={`${titleSlug}__new`}>New {props.buttonTerm}</QESButton></Link>
          }
        </header>
        <PageSection>
          <div className="container mt-3 px-2">
            <h2 className="lead">Previous {props.title} Reports</h2>

            <AccordionContainer className="mt-3">
              <Accordion title="Today" defaultOpen={true} testId="bulk-provider-change-reports__today-accordion">
                <Waffles<ProviderChangeReportsTableData>
                  columns={providerChangeColumnConfig}
                  customSort={customSort}
                  data={reportsToday}
                  fullWidth={true}
                  testId={`${titleSlug}-reports-today`}
                />
              </Accordion>
            </AccordionContainer>

            <AccordionContainer className="mt-3">
              <Accordion title="Last 7 Days" testId="bulk-provider-change-reports__last-7-days-accordion">
                <Waffles<ProviderChangeReportsTableData>
                  columns={providerChangeColumnConfig}
                  customSort={customSort}
                  data={reportsLast7}
                  fullWidth={true}
                  testId={`${titleSlug}-reports-last-7`}
                />
              </Accordion>
            </AccordionContainer>

            <AccordionContainer className="mt-3">
              <Accordion title="Last 30 Days" testId="bulk-provider-change-reports__last-30-days-accordion">
                <Waffles<ProviderChangeReportsTableData>
                  columns={providerChangeColumnConfig}
                  customSort={customSort}
                  data={reportsLast30}
                  fullWidth={true}
                  testId={`${titleSlug}-reports-last-30`}
                />
              </Accordion>
            </AccordionContainer>
          </div>
          <span className="d-none" ref={downloadRef} />
        </PageSection>
      </PageInner>
    </Page>
  );
};

export default BulkProviderChangeReports;
